import { Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ConfirmationDialog } from "../../../common/components/functional/confirmationDialog/ConfirmationDialog";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { isoFormat } from "../../../common/helpers/formatDateTime";
import useIsLoading from "../../../hooks/useIsLoading";
import { TalentBlacklist } from "../../talents/talentAPI";
import {
  addBlacklistEntry,
  getBlacklistByTalentId,
  removeBlacklistEntry,
  updateBlacklistEntry,
} from "../../talents/talentsSlice";
import { AddBlacklistDialog } from "./AddBlacklistDialog";
import { BlacklistTable } from "./BlacklistTable";
import {
  BlacklistFormValues,
  mapFormToAddBlacklistPayload,
  mapFormToUpdateBlacklistPayload,
} from "./blacklistMappers";

type Props = {
  isAddDialogOpen: boolean;
  onAddDialogClose: () => void;
};

export const BlacklistSection = ({
  isAddDialogOpen,
  onAddDialogClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { talentId } = useParams();
  const { localized } = useAppSelector((state) => state);
  const isLoading = useIsLoading(["talents"]);

  const [isConfirmDialogOpen, setIsConfirmDialogOpen] =
    useState<boolean>(false);
  const [blacklists, setBlacklists] = useState<TalentBlacklist[]>([]);
  const [entryToDelete, setEntryToDelete] = useState<{
    blacklistEntryId: string;
    version: number;
  } | null>(null);
  const [editingEntry, setEditingEntry] = useState<TalentBlacklist | null>(
    null,
  );
  const [isBlacklistTableOpen, setIsBlacklistTableOpen] = useState(true);

  const isEditDialogOpen = isAddDialogOpen || !!editingEntry;

  const fetchBlacklists = useCallback(() => {
    if (!talentId) return;
    dispatch(getBlacklistByTalentId(talentId)).then((action) => {
      if (isFulfilled(action)) {
        setBlacklists(action.payload);
      }
    });
  }, [dispatch, talentId]);

  const handleSaveBlacklist = (formData: BlacklistFormValues) => {
    if (!talentId) return;

    if (editingEntry) {
      const body = mapFormToUpdateBlacklistPayload(formData, editingEntry);
      dispatch(updateBlacklistEntry(body)).then((action) => {
        if (isFulfilled(action)) {
          const updatedEvent = action.payload.find(
            (e) => e.name === "blEntryChg",
          );
          if (updatedEvent) {
            setBlacklists((prev) =>
              prev.map((entry) =>
                entry.id === editingEntry.id
                  ? {
                      ...entry,
                      customerOrganizationId: formData.customerOrganization?.id,
                      customerOrganizationName:
                        formData.customerOrganization?.name,
                      workLocationId: formData.workLocation?.id,
                      workLocationName: formData.workLocation?.name,
                      workTitles: formData.workTitles.map((wt) => ({
                        id: wt.id,
                        name: localized.workTitle[wt.id] ?? wt.id,
                      })),
                      reason: formData.reason,
                      version: updatedEvent.version,
                    }
                  : entry,
              ),
            );
          }
          handleCloseDialog();
        }
      });
    } else {
      const data = mapFormToAddBlacklistPayload(formData, talentId);
      dispatch(addBlacklistEntry(data)).then((action) => {
        if (isFulfilled(action)) {
          const newBlacklist: TalentBlacklist = {
            id: action.payload.id,
            talentId,
            blockingParty: data.blockingParty,
            customerCompanyId: data.customerCompanyId,
            customerCompanyName: formData.customerCompany?.name,
            customerOrganizationId: data.customerOrganizationId,
            customerOrganizationName: formData.customerOrganization?.name,
            workLocationId: data.workLocationId,
            workLocationName: formData.workLocation?.name,
            workTitles: formData.workTitles.map((workTitle) => ({
              id: workTitle.id,
              name: localized.workTitle[workTitle.id] ?? workTitle.id,
            })),
            reason: formData.reason,
            createdAt: format(new Date(), isoFormat),
            version: action.payload.version,
          };
          setBlacklists((prev) => [...prev, newBlacklist]);
          handleCloseDialog();
        }
      });
    }
  };

  const setupDelete = (blacklistEntryId: string, version: number) => {
    setEntryToDelete({ blacklistEntryId, version });
    setIsConfirmDialogOpen(true);
  };

  const deleteBlacklistRow = () => {
    if (!entryToDelete) return;
    const { blacklistEntryId, version } = entryToDelete;
    dispatch(removeBlacklistEntry({ blacklistEntryId, version })).then(
      (action) => {
        if (isFulfilled(action)) {
          setBlacklists(
            blacklists.filter((blacklist) => blacklist.id !== blacklistEntryId),
          );
          setIsConfirmDialogOpen(false);
          setEntryToDelete(null);
        }
      },
    );
  };

  const handleCloseDialog = () => {
    onAddDialogClose();
    setEditingEntry(null);
  };

  useEffect(() => {
    if (!talentId) return;
    fetchBlacklists();
  }, [fetchBlacklists, talentId]);

  if (!talentId) return <LoadingSpinner />;

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      <Typography variant="header" fontWeight={600} fontSize="20px" m={1}>
        {t("talentProfile:blacklistTitle")}
      </Typography>
      <BlacklistTable
        blacklists={blacklists}
        onEditClick={setEditingEntry}
        deleteBlacklistRow={setupDelete}
        isCollapsed={!isBlacklistTableOpen}
        onToggleCollapse={() => setIsBlacklistTableOpen((prev) => !prev)}
      />
      <AddBlacklistDialog
        isOpen={isEditDialogOpen}
        editingEntry={editingEntry}
        saveBlacklist={handleSaveBlacklist}
        closeDialog={handleCloseDialog}
        isLoading={isLoading}
      />
      <ConfirmationDialog
        title={t("talentProfile:confirmBlacklistDelete")}
        contentText={t("talentProfile:confirmBlacklistDeleteContent")}
        cancelText={t("common:cancel")}
        confirmText={t("common:delete")}
        isOpen={isConfirmDialogOpen}
        onConfirm={deleteBlacklistRow}
        onCancel={() => setIsConfirmDialogOpen(false)}
      />
    </>
  );
};
